import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sun, Cloud, CloudRain, CloudSnow } from 'lucide-react';
import { useCommandStore } from '../../store/commandStore.js';
import { getWeather, type WeatherData } from '../../lib/weather.js';

const WEATHER_ICONS: Record<string, typeof Sun> = {
  sun: Sun,
  partly: Cloud,
  cloud: Cloud,
  rain: CloudRain,
  storm: CloudRain,
  fog: Cloud,
  wind: Cloud,
  snow: CloudSnow,
};

interface WeatherOverlayProps {
  lat: number;
  lng: number;
  hour: number;
}

export default function WeatherOverlay({ lat, lng, hour }: WeatherOverlayProps) {
  const { layerVisibility } = useCommandStore();
  const [weather, setWeather] = useState<WeatherData | null>(null);

  useEffect(() => {
    if (!layerVisibility.weather) {
      setWeather(null);
      return;
    }
    getWeather(lat, lng).then(setWeather);
  }, [lat, lng, layerVisibility.weather]);

  if (!layerVisibility.weather || !weather) return null;

  const Icon = WEATHER_ICONS[weather.iconKey] || Cloud;
  const displayTemp = weather.hourly[hour]?.temp ?? weather.temp;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.22 }}
        className="weather-overlay"
      >
        <Icon size={18} />
        <span className="weather-overlay-temp">{displayTemp}°</span>
        <span className="weather-overlay-desc">{weather.description}</span>
      </motion.div>
    </AnimatePresence>
  );
}
